import { RootState } from "../store";
import { AccessControl } from "../actions/authActions";

/**
 * Selector to get the access control object from Redux state
 * @param state - Root Redux state
 * @returns AccessControl object or null
 */
export const selectAccess = (state: RootState): AccessControl | null => {
  return state.auth.user?.access || null;
};

/** Sidebar ids granted per user, or null when role flags alone decide. */
export const selectModuleGrants = (state: RootState): string[] | null =>
  state.auth.user?.moduleGrants ?? null;

/**
 * Selector to check if user has access to a specific feature
 * @param state - Root Redux state
 * @param feature - Feature name to check (e.g., 'meals', 'booking', 'settings')
 * @returns boolean indicating if user has access
 */
export const hasAccess = (state: RootState, feature: keyof AccessControl): boolean => {
  const access = selectAccess(state);
  return access?.[feature] === true;
};

/**
 * Selector to get all access permissions
 * @param state - Root Redux state
 * @returns Object with all access permissions or empty object
 */
export const getAllAccess = (state: RootState): AccessControl => {
  return selectAccess(state) || {};
};

/**
 * Helper function to check access outside of React components
 * This reads from localStorage directly
 * @param feature - Feature name to check
 * @returns boolean indicating if user has access
 */
export const checkAccessFromStorage = (feature: keyof AccessControl): boolean => {
  try {
    const stored = localStorage.getItem('remnutri_access');
    if (!stored) return false;
    const access: AccessControl = JSON.parse(stored);
    return access[feature] === true;
  } catch {
    return false;
  }
};

/**
 * Helper function to get all access from localStorage
 * Useful for non-React contexts
 * @returns AccessControl object or null
 */
export const getAccessFromStorage = (): AccessControl | null => {
  try {
    const stored = localStorage.getItem('remnutri_access');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

